// CSV files for the admin: the bank file for a payout run, and the accounting
// export of paid invoices. Several fields come from what hosts typed (names,
// invoice numbers), so every text cell is made safe against spreadsheet formula
// injection: a cell that starts with = + - @ (or a tab/return) would otherwise
// run as a formula when the file is opened in Excel or Sheets.

/** A text cell: formula-safe and quoted when it needs to be. */
export function csvText(value: string | null | undefined): string {
  let s = value ?? '';
  if (/^[=+\-@\t\r]/.test(s)) s = `'${s}`;
  if (/[",\n\r;]/.test(s)) s = `"${s.replace(/"/g, '""')}"`;
  return s;
}

/** Cents as 12.34 (a point, no thousands separator): what bank files expect. */
export function csvAmount(cents: number | null | undefined): string {
  return ((cents ?? 0) / 100).toFixed(2);
}

function csv(header: string[], rows: string[][]): string {
  return [header.join(','), ...rows.map((r) => r.join(','))].join('\r\n') + '\r\n';
}

export interface BankInvoice {
  /** Sekretly's reference, e.g. SEK-2026-0007. */
  number: string;
  /** The host's own invoice number. */
  invoice_number: string;
  payout_account_name: string | null;
  payout_iban: string | null;
  invoice_payable_cents: number;
  currency: string;
}

/** What the bank needs for each transfer: who, where, how much, and a reference. */
export function bankCsv(invoices: BankInvoice[]): string {
  return csv(
    ['Beneficiary', 'IBAN', 'Amount', 'Currency', 'Reference'],
    invoices.map((i) => [
      csvText(i.payout_account_name),
      csvText((i.payout_iban ?? '').replace(/\s/g, '')),
      csvAmount(i.invoice_payable_cents),
      csvText(i.currency.toUpperCase()),
      csvText(`Sekretly ${i.number} ${i.invoice_number}`.slice(0, 140)),
    ]),
  );
}

export interface AccountingRow {
  number: string;
  host_name: string | null;
  host_nif: string | null;
  invoice_kind: string;
  invoice_number: string;
  invoice_date: string;
  invoice_base_cents: number;
  invoice_vat_cents: number;
  invoice_vat_exemption: string | null;
  total_cents: number;
  invoice_withholding_cents: number;
  invoice_payable_cents: number;
  currency: string;
  paid_at: string | null;
  payment_reference: string | null;
  trips: number;
}

/** One line per paid invoice: what the accountant needs (VAT, tax withheld, who was paid when). */
export function accountingCsv(rows: AccountingRow[]): string {
  return csv(
    ['Paid on', 'Host', 'Host NIF', 'Type', 'Invoice no.', 'Invoice date', 'Base', 'VAT', 'VAT exemption',
      'Total', 'Tax withheld', 'Paid', 'Currency', 'Bank reference', 'Sekretly ref.', 'Trips'],
    rows.map((r) => [
      csvText(r.paid_at ? r.paid_at.slice(0, 10) : ''),
      csvText(r.host_name),
      csvText(r.host_nif),
      csvText(r.invoice_kind === 'recibo_verde' ? 'Recibo verde' : 'Fatura'),
      csvText(r.invoice_number),
      csvText(r.invoice_date),
      csvAmount(r.invoice_base_cents),
      csvAmount(r.invoice_vat_cents),
      csvText(r.invoice_vat_exemption),
      csvAmount(r.total_cents),
      csvAmount(r.invoice_withholding_cents),
      csvAmount(r.invoice_payable_cents),
      csvText(r.currency.toUpperCase()),
      csvText(r.payment_reference),
      csvText(r.number),
      String(r.trips),
    ]),
  );
}

/** Save text as a file in the browser (with a byte-order mark so Excel reads accents correctly). */
export function downloadCsv(filename: string, content: string): void {
  const blob = new Blob(['﻿', content], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// ── The proof of payment ──────────────────────────────────────────────────────

export const PROOF_FILE_TYPES = ['application/pdf', 'image/jpeg', 'image/png'];
export const PROOF_FILE_MAX_BYTES = 10 * 1024 * 1024;

export function proofFileError(file: { type: string; size: number } | null): string | null {
  if (!file) return 'Attach the bank’s confirmation of the payment (PDF, JPG or PNG).';
  if (!PROOF_FILE_TYPES.includes(file.type)) return 'The proof must be a PDF, JPG or PNG.';
  if (file.size <= 0) return 'This file is empty.';
  if (file.size > PROOF_FILE_MAX_BYTES) return 'The file is too big. The limit is 10 MB.';
  return null;
}
